import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from "@simplewebauthn/browser";
import CustomModal from "../ui/modal/modal.component";
import { RegisterPasskeyForm } from "../../workspaces/profile/managePasskeys/registerPasskeyForm.component";
import { useAuthContext } from "../../contexts/auth";
import { useWebAuthnCredentials } from "../../api/webauthn";
import axios from "../../api/axios";
import { BIOMETRIC_SETUP_OFFERED_ENDPOINT } from "../../consts";

export const SetupBiometricLoginModal = () => {
  const { t } = useTranslation();
  const { currentUser, setCurrentUser } = useAuthContext();
  const { list } = useWebAuthnCredentials();
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    // Wait until the profile-completion prompt is out of the way so the two
    // modals never compete for attention on the same login.
    if (!currentUser || !currentUser.first_name || !currentUser.last_name) return;
    // Offered once per user, ever - remembered on the backend, so neither
    // reopening the browser nor another device brings it back.
    if (currentUser.biometric_setup_offered_at) return;
    if (!browserSupportsWebAuthn()) return;

    let cancelled = false;
    (async () => {
      // A device without Face ID / Touch ID / Windows Hello doesn't use up the
      // offer - it's made on the first device that can take it.
      const hasPlatformAuthenticator = await platformAuthenticatorIsAvailable();
      if (!hasPlatformAuthenticator || cancelled) return;

      const credentials = await list();
      if (cancelled) return;
      // Shown now, or not needed at all (a passkey already exists) - either
      // way it's never offered again.
      markOffered();
      if (credentials.length === 0) setOpened(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const markOffered = () => {
    if (currentUser) {
      setCurrentUser({ ...currentUser, biometric_setup_offered_at: new Date().toISOString() });
    }
    axios.post(BIOMETRIC_SETUP_OFFERED_ENDPOINT).catch(() => {
      // Not recorded - the prompt may show once more on a later visit.
    });
  };

  const handleDismiss = (value: boolean) => {
    setOpened(value);
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t("Set up biometric login (Face ID, Touch ID, Windows Hello) for faster, more secure sign-in next time. You can also do this later from your profile settings.")}
      </Typography>
      <RegisterPasskeyForm onSuccess={() => handleDismiss(false)} />
      <Button onClick={() => handleDismiss(false)} fullWidth>
        {t("Maybe later")}
      </Button>
    </Box>
  );

  return (
    <CustomModal
      opened={opened}
      setOpened={handleDismiss}
      title={t("Set up biometric login")}
      content={content}
      width={500}
    />
  );
};
